import dayjs from "dayjs";

export const SCHEMA_VERSION = 3;

/**
 * The stamp for a value nobody chose: a default supplied because there was
 * nothing to read. Zero is older than any real change, so a value someone
 * actually picked always beats it — one already in the account, or one
 * arriving from another device later.
 *
 * Getting this wrong is not a cosmetic bug. A default stamped with the clock
 * outranks the real settings in the account, wins the merge against all of
 * them, and is then uploaded over them. Both places that build a document
 * from defaults have made that mistake; the constant exists so the rule is
 * greppable and the next one does not.
 *
 * Migrated *ticks* also carry a bare 0, for a related but distinct reason —
 * they are real user data of unknown age, not invented values. See
 * migrateLegacy.
 */
export const UNCHOSEN = 0;

/** Every setting the document carries. startDate and numberOfDays are one field. */
// Derived from defaults() so the two can never drift apart. `defaults` is a
// hoisted function declaration, so calling it here is safe.
export const SETTING_KEYS = Object.keys(defaults(0));

/** Defaults depend on the clock, so this is a function rather than a constant. */
export function defaults(now = Date.now()) {
  return {
    title: "My SCHEDULE",
    // A column is { id, name }. Ticks hang off the id, so a rename changes only
    // the name and never moves a tick.
    slots: [
      { id: "morning", name: "morning" },
      { id: "noon", name: "noon" },
      { id: "evening", name: "evening" },
    ],
    dateRange: { startDate: dayjs(now).format("YYYY-MM-DD"), numberOfDays: 7 },
    showWeekdays: false,
    shouldPrintHeader: false,
    repeatStep: 1,
    repeatUnit: "day",
    dateFormat: "YYYY-MM-DD",
    weekdayFormat: "ddd",
  };
}

export function emptyDoc(now = Date.now()) {
  const values = defaults(now);
  const settings = {};
  // A fresh install has chosen none of these, so none of them may outrank what
  // the account already holds. `now` still decides what the defaults *are*
  // (dateRange.startDate is today), just not what authority they carry.
  for (const key of SETTING_KEYS) settings[key] = { value: values[key], updatedAt: UNCHOSEN };
  return { schemaVersion: SCHEMA_VERSION, settings, ticks: {} };
}

/** The one place a row key is derived. Never the display format. */
export const keyForDay = (day) => dayjs(day).format("YYYY-MM-DD");

export function tickedSlots(ticks, day) {
  const row = (ticks || {})[keyForDay(day)] || {};
  return Object.keys(row).filter((slot) => row[slot] && row[slot].on);
}

export function setTick(doc, day, slot, on, now = Date.now()) {
  const key = keyForDay(day);
  return {
    ...doc,
    ticks: { ...doc.ticks, [key]: { ...(doc.ticks[key] || {}), [slot]: { on: !!on, updatedAt: now } } },
  };
}

export function setSetting(doc, key, value, now = Date.now()) {
  return { ...doc, settings: { ...doc.settings, [key]: { value, updatedAt: now } } };
}

export const LEGACY_KEYS = [
  "startDate",
  "numberOfDays",
  "history",
  "slotNames",
  "title",
  "showWeekdays",
  "shouldPrintHeader",
  "repeatStep",
  "repeatUnit",
  "dateFormat",
  "weekdayFormat",
];

const parseJSON = (raw, fallback) => {
  if (raw === null || raw === undefined) return fallback;
  try {
    const value = JSON.parse(raw);
    return value === null ? fallback : value;
  } catch {
    return fallback;
  }
};

/**
 * Resolve a legacy history key back to an ISO date.
 * The legacy key was `day.format(dateFormat)`, and formats like DD and MMM are
 * not unique, so the only honest way back is to test it against the days that
 * were actually on screen. Ambiguous means dropped, never guessed.
 */
function resolveLegacyKey(key, dateFormat, range) {
  // An already-ISO key needs no range: of the four offered display formats only
  // YYYY-MM-DD can produce this shape, so it stands on its own. This matters
  // because the legacy history map was never pruned — old onHistoryUpdate only
  // ever added keys — so it holds every day the user ever ticked, while the
  // range is a seven-day window by default. Resolving ISO keys against the range
  // would silently delete months of ticks on first load.
  if (/^\d{4}-\d{2}-\d{2}$/.test(key) && dayjs(key).isValid()) return key;

  const matches = range.filter((day) => day.format(dateFormat) === key);
  return matches.length === 1 ? keyForDay(matches[0]) : null;
}

const isSlotList = (slots) =>
  !!slots &&
  Array.isArray(slots.value) &&
  slots.value.every((slot) => slot && typeof slot.id === "string" && typeof slot.name === "string");

/**
 * Version 2 -> 3: columns get hidden IDs. Pure; the input is never touched.
 *
 * An existing column's ID is its name, because that is what its ticks are
 * already filed under — nothing has to move. Two columns can share a name, and
 * their ticks were already shared; the second and later ones get a suffix and a
 * copy of the shared ticks, so every column still shows what it showed. The
 * suffix uses the unit separator because no one types it into a column name.
 *
 * A rolled-back 0.5.19 can leave a document with both `slots` and `slotNames`.
 * The newer list wins, and a zero-stamped `slots` never beats a real one.
 */
export function migrateV2(doc) {
  const { slotNames, slots } = doc.settings || {};
  const namesStamp = typeof slotNames?.updatedAt === "number" ? slotNames.updatedAt : 0;
  let ticks = doc.ticks || {};
  let chosen;

  if (isSlotList(slots) && slots.updatedAt >= (slotNames ? namesStamp : -Infinity)) {
    chosen = slots;
  } else {
    const names = slotNames?.value;
    if (Array.isArray(names) && names.every((name) => typeof name === "string")) {
      const seen = new Map();
      const value = names.map((name) => {
        const n = (seen.get(name) || 0) + 1;
        seen.set(name, n);
        return { id: n === 1 ? name : `${name}\u001f${n}`, name };
      });
      ticks = Object.fromEntries(
        Object.entries(ticks).map(([day, row]) => {
          // A corrupt (non-object) row has nothing to copy; leave it exactly as it was.
          if (!row || typeof row !== "object") return [day, row];
          const copy = { ...row };
          for (const slot of value) {
            // hasOwn, so a column named "toString" does not copy a prototype member.
            if (
              slot.id !== slot.name &&
              Object.prototype.hasOwnProperty.call(row, slot.name) &&
              row[slot.name] !== undefined &&
              copy[slot.id] === undefined
            ) {
              copy[slot.id] = { ...row[slot.name] };
            }
          }
          return [day, copy];
        })
      );
      chosen = { value, updatedAt: namesStamp };
    } else {
      // A malformed newer slotNames must not throw away a valid slots.
      chosen = isSlotList(slots) ? slots : { value: defaults(0).slots, updatedAt: UNCHOSEN };
    }
  }

  // `slots` takes the place `slotNames` had, so the key order matches a fresh
  // document. merge keeps local key order and the upload check compares JSON
  // strings, so a different order would re-upload on every sync.
  const settings = {};
  let placed = false;
  for (const [key, setting] of Object.entries(doc.settings || {})) {
    if (key === "slotNames") {
      settings.slots = chosen;
      placed = true;
    } else if (key !== "slots") settings[key] = setting;
  }
  if (!placed) settings.slots = chosen;

  return { ...doc, schemaVersion: SCHEMA_VERSION, settings, ticks };
}

/** Bring a stored or downloaded document up to the current version. Returns the same object when nothing is needed. */
export function upgradeDoc(doc) {
  if (!(doc.schemaVersion >= 3) || (doc.settings || {}).slotNames !== undefined) return migrateV2(doc);
  return doc;
}

/**
 * A fresh column ID. Random, so two devices adding a column offline never pick
 * the same one. It must not equal an existing ID or any key in a tick row, or a
 * new column would inherit old ticks.
 */
export function newSlotId(slots, ticks) {
  const taken = new Set(slots.map((slot) => slot.id));
  for (const row of Object.values(ticks || {})) for (const key of Object.keys(row || {})) taken.add(key);
  for (;;) {
    const bytes = crypto.getRandomValues(new Uint8Array(6));
    const id = "c-" + Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
    if (!taken.has(id)) return id;
  }
}

export function migrateLegacy(read, now = Date.now()) {
  const present = LEGACY_KEYS.some((key) => read(key) != null);
  if (!present) return null;

  const fallback = defaults(now);
  const dateFormat = read("dateFormat") || fallback.dateFormat;

  // The legacy startDate was written with the *display* format, so it only
  // carries a year when that format had one. dayjs("08-30") parses to
  // 2001-08-30 — valid, and wrong by 25 years — so a format without YYYY makes
  // the stored value unrecoverable rather than merely ambiguous.
  // However, if the stored value itself contains a year (four consecutive digits),
  // we can still use it.
  const rawStart = read("startDate");
  const start = rawStart && (/YYYY/.test(dateFormat) || /\d{4}/.test(rawStart)) ? dayjs(rawStart) : dayjs(null);

  const numberOfDays = parseInt(read("numberOfDays"), 10);
  const days = Number.isFinite(numberOfDays) && numberOfDays > 0 ? numberOfDays : fallback.dateRange.numberOfDays;

  const range = [];
  if (start.isValid()) for (let i = 0; i < days; i++) range.push(start.add(i, "day"));

  const ticks = {};
  const legacyHistory = parseJSON(read("history"), {});
  for (const [legacyKey, names] of Object.entries(legacyHistory)) {
    if (!Array.isArray(names) || names.length === 0) continue;
    const iso = resolveLegacyKey(legacyKey, dateFormat, range);
    if (!iso) continue;
    ticks[iso] = ticks[iso] || {};
    for (const name of names) ticks[iso][name] = { on: true, updatedAt: 0 };
  }

  // Built as a version-2 document, then run through migrateV2, so the legacy
  // path and the stored-v2 path cannot disagree about what a column's ID is.
  const { slots: _slots, ...fallbackV2 } = fallback;
  const values = {
    ...fallbackV2,
    // `||` not `??`: an empty stored title fell back to the default before, and
    // this refactor is not the place to change that.
    title: read("title") || fallback.title,
    slotNames: parseJSON(read("slotNames"), fallback.slots.map((slot) => slot.name)),
    showWeekdays: parseJSON(read("showWeekdays"), fallback.showWeekdays),
    shouldPrintHeader: parseJSON(read("shouldPrintHeader"), fallback.shouldPrintHeader),
    repeatStep: parseInt(read("repeatStep"), 10) || fallback.repeatStep,
    repeatUnit: read("repeatUnit") || fallback.repeatUnit,
    dateFormat,
    weekdayFormat: read("weekdayFormat") || fallback.weekdayFormat,
    // The day count is format-independent, so it survives even when the start
    // date does not.
    dateRange: {
      startDate: start.isValid() ? keyForDay(start) : fallback.dateRange.startDate,
      numberOfDays: days,
    },
  };

  // Which legacy key each setting was read from above. A setting whose keys were
  // all absent was not migrated at all — it is a default this function invented,
  // and it gets no authority. The distinction matters most for the settings that
  // post-date the old build entirely (weekdayFormat, showWeekdays): it never had
  // a key for them, so stamping them `now` sent pure filler up to overwrite the
  // real values another device had already saved.
  const storedFor = {
    title: ["title"],
    slotNames: ["slotNames"],
    showWeekdays: ["showWeekdays"],
    shouldPrintHeader: ["shouldPrintHeader"],
    repeatStep: ["repeatStep"],
    repeatUnit: ["repeatUnit"],
    dateFormat: ["dateFormat"],
    weekdayFormat: ["weekdayFormat"],
    // One field, two legacy keys: either one means the user had a range.
    dateRange: ["startDate", "numberOfDays"],
  };

  const settings = {};
  for (const key of [...SETTING_KEYS.filter((key) => key !== "slots"), "slotNames"]) {
    // A key missing from storedFor falls to UNCHOSEN, which is the safe side: a
    // setting added later and forgotten here loses to real data rather than
    // overwriting it.
    const chosen = (storedFor[key] || []).some((legacyKey) => read(legacyKey) != null);
    settings[key] = { value: values[key], updatedAt: chosen ? now : UNCHOSEN };
  }
  return migrateV2({ schemaVersion: 2, settings, ticks });
}

export const LOCAL_KEY = "doc";

/** The raw document, kept the first time we upgrade it. Nothing reads it; it is for rescue by hand. Usually version 2, but it can be a version 3 carrying a stray slotNames (a rollback). */
export const BACKUP_KEY = "docV2Backup";

/**
 * Fill any settings key a stored document lacks. `Main` reads
 * `doc.settings[key].value` directly, so one missing key is a white screen on
 * boot with no way back. Filler is stamped 0 so any real value beats it —
 * including one arriving later from another device.
 */
function withAllSettings(doc, now) {
  const values = defaults(now);
  const settings = { ...doc.settings };
  for (const key of SETTING_KEYS) {
    if (!settings[key] || settings[key].value === undefined) {
      settings[key] = { value: values[key], updatedAt: UNCHOSEN };
    }
  }
  return { ...doc, settings };
}

export function readLocal(now = Date.now()) {
  const raw = localStorage.getItem(LOCAL_KEY);

  // A document key that EXISTS but will not parse must never fall through to
  // migration. The legacy keys froze at the first migration, so rebuilding from
  // them would silently replace months of ticks with a stale snapshot — and
  // nothing would surface the loss. Absent and corrupt are different states.
  if (raw !== null) {
    const stored = parseJSON(raw, null);
    if (stored && stored.settings && stored.ticks) {
      const upgraded = upgradeDoc(stored);
      if (upgraded === stored) return withAllSettings(stored, now);
      try {
        if (localStorage.getItem(BACKUP_KEY) === null) localStorage.setItem(BACKUP_KEY, raw);
      } catch {
        // The backup is optional; a full disk must not stop the app opening.
      }
      const doc = withAllSettings(upgraded, now);
      writeLocal(doc);
      return doc;
    }
    const fresh = emptyDoc(now);
    writeLocal(fresh);
    return fresh;
  }

  // First run after the upgrade: fold the legacy keys in, and leave them alone
  // afterwards so rolling back a deploy loses nothing.
  const migrated = migrateLegacy((key) => localStorage.getItem(key), now);
  const doc = migrated || emptyDoc(now);
  writeLocal(doc);
  return doc;
}

export function writeLocal(doc) {
  localStorage.setItem(LOCAL_KEY, JSON.stringify(doc));
}

export function resetDoc(doc, now = Date.now()) {
  const values = defaults(now);
  const settings = {};
  for (const key of SETTING_KEYS) settings[key] = { value: values[key], updatedAt: now };

  const ticks = {};
  for (const [day, row] of Object.entries(doc.ticks || {})) {
    ticks[day] = {};
    for (const slot of Object.keys(row)) ticks[day][slot] = { on: false, updatedAt: now };
  }
  return { schemaVersion: SCHEMA_VERSION, settings, ticks };
}
